import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthorizationError } from '@ametista-ai/api-schema/errors';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import { useNavigate } from 'react-router-dom';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { DatesProvider, DayOfWeek } from '@mantine/dates';
import { Navbar } from '@/components/Navbar';
import { TimezoneSyncEffect } from '@/components/TimezoneSync/TimezoneSyncEffect';
import { UiScaleEffect } from '@/components/UiScale/UiScaleEffect';
import { FlagsProvider } from '@/flags/FlagsProvider';
import { useCurrentAgency } from '@/queries/agencies/useCurrentAgency';
import { ensureCdnCookies } from '@/queries/auth/authActions';
import { meKeys } from '@/queries/me/keys';
import { fetchMe, useMe } from '@/queries/me/useMe';
import { Layout } from '@/ui';
import { LANGUAGE_TO_DAYJS_LOCALE, setLocale } from '@/utils/i18n';
import { config } from '../../../config';
import { CreatePostModal } from '../CreatePostModal';
import { SearchSpotlight } from '../Search/SearchSpotlight';
import { Bootstrap } from './Bootstrap';

declare global {
  interface Window {
    _uxa?: any;
  }
}

export const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const { data: me } = useMe({ enabled: false });
  const { data: agency } = useCurrentAgency();
  const navigate = useNavigate();
  // BrowserRouter's navigate identity changes on every pathname change — keep a ref
  // so fetchSession stays stable and /me is only fetched once on mount.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [fetchError, setFetchError] = useState<Error | null>(null);
  const [isFetching, setIsFetching] = useState(true);

  const fetchSession = useCallback(async () => {
    setFetchError(null);
    setIsFetching(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: meKeys.all,
        queryFn: () => fetchMe(queryClient),
      });
      if (!data?.user) {
        navigateRef.current('/auth/login');
        return;
      }
      if (!data?.agency) {
        navigateRef.current('/onboarding');
        return;
      }

      if (data.agency!.status === 'pending') {
        navigateRef.current(`/onboarding/agency/${data.agency!.id}`);
        return;
      }

      setLocale(data.user.language, data.user.locale);

      Sentry.setUser({
        id: data.user.id,
        email: data.user.email,
      });

      if (import.meta.env.PROD ?? true) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore Canny
        Canny('identify', {
          appID: config.canny.appId,
          user: {
            email: data.user.email,
            name: data.user.name,
            id: data.user.id,
            companies: data.agency
              ? [
                  {
                    id: data.agency.id,
                    name: data.agency.name,
                  },
                ]
              : [],
          },
        });
      }

      try {
        const mixpanelToken = config.mixpanel.token;

        if (mixpanelToken && (import.meta.env.PROD ?? true)) {
          mixpanel.init(mixpanelToken, {
            autocapture: true,
            record_sessions_percent: 50,
            record_block_selector: '',
          });
          mixpanel.identify(data.user.id);
          mixpanel.people.set({
            $name: data.user.displayName,
            $email: data.user.email,
          });
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error('Mixpanel identify failed', error);
      }

      try {
        if (import.meta.env.PROD ?? true) {
          (window._uxa as any)?.push?.(['identify', data.user.id]);
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error('Contentsquare identify failed', error);
      }

      ensureCdnCookies();
    } catch (error) {
      console.error(error);
      if (error instanceof AuthorizationError) {
        navigateRef.current('/auth/login');
      } else {
        Sentry.captureException(error);
        setFetchError(error instanceof Error ? error : new Error(String(error)));
      }
    } finally {
      setIsFetching(false);
    }
  }, [queryClient]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  if (fetchError) {
    return (
      <Center h="100vh">
        <Stack align="center">
          <Text c="dimmed">Something went wrong. Please try again.</Text>
          <Button onClick={fetchSession}>Retry</Button>
        </Stack>
      </Center>
    );
  }

  if (isFetching) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  if (!agency || agency.status === 'pending') {
    return null;
  }

  const locale =
    me?.user?.locale ??
    LANGUAGE_TO_DAYJS_LOCALE?.[me?.user?.language as keyof typeof LANGUAGE_TO_DAYJS_LOCALE] ??
    'en';

  return (
    <FlagsProvider agency={agency} ldKey={config.launchDarkly.clientId}>
      <DatesProvider
        settings={{
          locale,
          firstDayOfWeek: dayjs().localeData().firstDayOfWeek() as DayOfWeek,
        }}
      >
        <Bootstrap>
          <UiScaleEffect />
          <TimezoneSyncEffect />
          <Layout leftSidebar={<Navbar />}>{children}</Layout>
          <SearchSpotlight />
          <CreatePostModal />
        </Bootstrap>
      </DatesProvider>
    </FlagsProvider>
  );
};
